import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import NavButtons from './NavButtons';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').substring(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

// ── Inline sign-in panel ──────────────────────────────────────────────────────
function SignInPanel({ onSuccess }) {
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [resetSent, setResetSent]     = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message || 'Sign in failed. Please check your details.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Enter your email above first, then click Forgot password.');
      return;
    }
    setResetLoading(true);
    setError('');
    try {
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/#/reset-password',
      });
      setResetSent(true);
    } catch (err) {
      setError('Could not send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignIn} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="field-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="input-field"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>
        <div className="field-group">
          <label className="form-label">Password</label>
          <input
            type="password"
            className="input-field"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
      </div>
      {error && (
        <p className="text-red-500 text-xs font-bold">{error}</p>
      )}
      {resetSent ? (
        <p className="text-sage-500 text-xs font-medium bg-sage-50 border border-sage-100 rounded-xl px-4 py-2.5">
          ✉️ Check your inbox — we sent a password reset link to <strong>{email}</strong>.
        </p>
      ) : (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={resetLoading}
            className="text-xs text-blush-400 font-bold hover:text-blush-500 transition-colors disabled:opacity-50"
          >
            {resetLoading ? 'Sending…' : 'Forgot password?'}
          </button>
        </div>
      )}
      <button
        type="submit"
        disabled={loading || !email || !password}
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        {loading ? (
          <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Signing in…</>
        ) : (
          <><SafeIcon name="LogIn" /> Sign In & Keep My Cart</>
        )}
      </button>
    </form>
  );
}

export default function Step3Customer({ formData, updateFormData, onNext, onBack, session, profileLoaded }) {
  const [emailTouched, setEmailTouched]       = useState(false);
  const [phoneTouched, setPhoneTouched]       = useState(false);
  const [recipPhoneTouched, setRecipPhoneTouched] = useState(false);
  const [showSignIn, setShowSignIn]           = useState(false);

  const isLoggedIn = !!session;

  const emailsMatch        = formData.customer_email === formData.confirm_email;
  const hasConfirmEmail    = !!formData.confirm_email;
  const passwordsMatch     = formData.password === formData.confirm_password;
  const hasConfirmPassword = !!formData.confirm_password;
  const emailValid         = !formData.customer_email || isValidEmail(formData.customer_email);
  const phoneDigits        = (formData.customer_phone || '').replace(/\D/g, '');
  const phoneValid         = phoneDigits.length === 0 || phoneDigits.length === 10;
  const isValid = isLoggedIn
    ? formData.customer_name &&
      formData.customer_email &&
      isValidEmail(formData.customer_email) &&
      formData.customer_phone &&
      phoneDigits.length === 10 &&
      formData.terms_accepted
    : formData.customer_name &&
      formData.customer_email &&
      isValidEmail(formData.customer_email) &&
      formData.customer_phone &&
      phoneDigits.length === 10 &&
      formData.confirm_email &&
      emailsMatch &&
      formData.password &&
      formData.password.length >= 6 &&
      formData.confirm_password &&
      passwordsMatch &&
      formData.terms_accepted;

  const cardClass = 'bg-white rounded-3xl p-6 md:p-8 border border-sage-100 shadow-sm space-y-6';

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    updateFormData({ customer_name: '', customer_email: '', confirm_email: '', customer_phone: '' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="text-center space-y-3">
        <h2 className="text-4xl md:text-5xl font-serif text-charcoal">Your Details</h2>
        <p className="text-sage-500 text-lg md:text-xl font-medium">So we can keep you updated on your order.</p>
      </div>

      {/* ── Sign in / account status — PROMINENT at top ── */}
      <div className={`rounded-3xl border-2 overflow-hidden ${isLoggedIn ? 'border-green-200 bg-green-50' : 'border-blush-200 bg-blush-50/60'}`}>
        {isLoggedIn ? (
          // Logged in confirmation
          <div className="p-6 flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center shrink-0">
              <SafeIcon name="CheckCircle" className="text-green-500 text-xl" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-serif text-charcoal">Signed in — your cart is saved</p>
              <p className="text-sm text-sage-500 mt-0.5">{session.user.email}</p>
              <button onClick={handleSignOut} className="text-xs text-blush-400 font-bold hover:text-blush-500 transition-colors mt-2">
                Sign out and use a different account
              </button>
            </div>
          </div>
        ) : (
          // Sign in prompt + collapsible form
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0">
                  <SafeIcon name="LogIn" className="text-blush-400" />
                </div>
                <div>
                  <p className="font-serif text-lg text-charcoal">Already have an account?</p>
                  <p className="text-xs text-sage-500">Sign in to keep your cart and track your order.</p>
                </div>
              </div>
              <button
                onClick={() => setShowSignIn(v => !v)}
                className="text-sm font-bold text-blush-400 hover:text-blush-500 transition-colors shrink-0 ml-4"
              >
                {showSignIn ? 'Cancel' : 'Sign In'}
              </button>
            </div>

            <AnimatePresence>
              {showSignIn && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="pt-2 border-t border-blush-100">
                    <SignInPanel onSuccess={() => setShowSignIn(false)} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* ── Info banner ── */}
      <div className="bg-blush-50 border border-blush-100 rounded-3xl p-6 flex items-start gap-5">
        <div className="bg-white text-blush-400 p-2 rounded-full shadow-sm shrink-0">
          <SafeIcon name="Info" />
        </div>
        <p className="text-sage-600 text-sm leading-relaxed">
          Your Kabob is prepared fresh from our home kitchen. After your order is placed and payment is confirmed, we will keep you updated until delivery.
        </p>
      </div>

      {/* ── Personal info ── */}
      <div className={cardClass}>
        <h3 className="form-label !text-blush-400 !text-sm">Your Information</h3>
        <div className="space-y-6">
          <div className="field-group">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              className="input-field"
              placeholder="Enter your name"
              value={formData.customer_name || ''}
              onChange={(e) => updateFormData({ customer_name: e.target.value })}
              autoComplete="name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="field-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                className={`input-field ${emailTouched && !emailValid ? 'border-red-300 bg-red-50' : ''}`}
                placeholder="you@example.com"
                value={formData.customer_email || ''}
                onChange={(e) => updateFormData({ customer_email: e.target.value })}
                onBlur={() => setEmailTouched(true)}
                autoComplete="email"
              />
              {emailTouched && !emailValid && (
                <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1">Please enter a valid email</p>
              )}
            </div>

            {!isLoggedIn && (
              <div className="field-group">
                <label className="form-label">Confirm Email *</label>
                <input
                  type="email"
                  className={`input-field ${hasConfirmEmail && !emailsMatch ? 'border-red-300 bg-red-50' : ''}`}
                  placeholder="Re-enter your email"
                  value={formData.confirm_email || ''}
                  onChange={(e) => updateFormData({ confirm_email: e.target.value })}
                  autoComplete="email"
                />
                {hasConfirmEmail && !emailsMatch && (
                  <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1">Emails do not match</p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="field-group">
              <label className="form-label">Your Phone *</label>
              <input
                type="tel"
                className={`input-field ${phoneTouched && !phoneValid ? 'border-red-300 bg-red-50' : ''}`}
                placeholder="(555) 000-0000"
                value={formData.customer_phone || ''}
                onChange={(e) => updateFormData({ customer_phone: formatPhone(e.target.value) })}
                onBlur={() => setPhoneTouched(true)}
                autoComplete="tel"
              />
              {phoneTouched && !phoneValid && (
                <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1">Please enter a complete 10-digit number</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Account section ── */}
      <div className={cardClass}>
        {isLoggedIn ? (
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center shrink-0">
              <SafeIcon name="CheckCircle" className="text-green-500 text-xl" />
            </div>
            <div className="flex-1 space-y-1">
              <p className="text-lg font-serif text-charcoal">Placing order with your account</p>
              <p className="text-sm text-sage-500">{session.user.email}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blush-50 rounded-full flex items-center justify-center shrink-0">
                <SafeIcon name="Lock" className="text-blush-400" />
              </div>
              <div>
                <span className="text-lg font-serif text-charcoal block">Create your order tracking account</span>
                <span className="text-sm text-sage-400">Used to log in and track your order status.</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="field-group">
                <label className="form-label">Password *</label>
                <input
                  type="password"
                  required
                  className="input-field"
                  placeholder="Minimum 6 characters"
                  value={formData.password || ''}
                  onChange={(e) => updateFormData({ password: e.target.value })}
                  autoComplete="new-password"
                />
                {formData.password && formData.password.length < 6 && (
                  <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1">Password must be at least 6 characters</p>
                )}
              </div>
              <div className="field-group">
                <label className="form-label">Confirm Password *</label>
                <input
                  type="password"
                  required
                  className={`input-field ${hasConfirmPassword && !passwordsMatch ? 'border-red-300 bg-red-50' : ''}`}
                  placeholder="Re-enter your password"
                  value={formData.confirm_password || ''}
                  onChange={(e) => updateFormData({ confirm_password: e.target.value })}
                  autoComplete="new-password"
                />
                {hasConfirmPassword && !passwordsMatch && (
                  <p className="text-red-500 text-[10px] font-bold uppercase tracking-wider mt-1">Passwords do not match</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Terms */}
        <div className="pt-6 border-t border-sage-50">
          <label className="flex items-start gap-4 cursor-pointer group">
            <div className="relative flex items-center justify-center mt-1">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={formData.terms_accepted === true}
                onChange={(e) => updateFormData({ terms_accepted: e.target.checked })}
              />
              <div className={`w-6 h-6 border-2 rounded-lg transition-all ${formData.terms_accepted ? 'bg-blush-300 border-blush-300' : 'border-sage-200 group-hover:border-blush-200'}`}></div>
              <SafeIcon name="Check" className="absolute text-white opacity-0 peer-checked:opacity-100 text-sm" />
            </div>
            <div className="space-y-2">
              <span className="text-sm font-semibold text-charcoal block leading-relaxed">I understand and agree that:</span>
              <ul className="text-xs text-sage-500 space-y-1.5 list-disc pl-4">
                <li>Your card will be charged when you place your order.</li>
                <li>Your Kabob is prepared fresh from our home kitchen. We will keep you updated until delivery.</li>
                <li>We hand deliver on your chosen date.</li>
              </ul>
            </div>
          </label>
        </div>
      </div>

      <NavButtons
        onNext={onNext}
        onBack={onBack}
        disabled={!isValid}
        nextLabel="Review Order"
        icon="Check"
      />
    </motion.div>
  );
}
